import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../components/UserContext';

// Org-wide portal switches, stored at orgs/{orgId}/portal/config — one boolean
// field per switch. Defaults fill anything the doc doesn't set, so an absent
// doc/field (or a read error) resolves to the default and the portal renders
// as it always has. Owners flip these from Configuración > Organización.
export const PORTAL_CONFIG_DEFAULTS = {
  hasTarifa: true,
  // Saldos (facturas por pagar + créditos a favor) in the portal. Opt-in:
  // absent doc/field keeps the portal exactly as it rendered before the
  // feature existed.
  showSaldos: false,
};

/**
 * Resolve the portal switches for the current viewer.
 *
 * Broker views never react to portal switches, so for owner/operator this
 * returns the defaults without subscribing. Client users get a live
 * subscription to their org's config doc (which Firestore rules let them
 * read, unlike the org doc itself).
 *
 * @returns {typeof PORTAL_CONFIG_DEFAULTS} Config with defaults filled in.
 */
export function usePortalConfig() {
  const { userData, isClient } = useUser();
  const org = userData?.org;
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!isClient || !org) {
      setConfig(null);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'orgs', org, 'portal', 'config'),
      (snap) => setConfig(snap.exists() ? snap.data() : null),
      () => setConfig(null) // permission/network error → defaults
    );
    return () => unsubscribe();
  }, [isClient, org]);

  return useMemo(() => ({ ...PORTAL_CONFIG_DEFAULTS, ...(config || {}) }), [config]);
}
